// Fictional "Dubok" furniture workshop, 30 employees, sole accountant at month-end.
// See README.md for the schema.
export default {
  id: 'accountant',
  occupationId: '66b280662a4e4285b87eb0d6',
  occupationName: { en: 'Accountant', ua: 'Бухгалтер' },
  format: 'ledger',
  title: {
    en: 'A week-end close as the only accountant',
    ua: 'Закриття місяця єдиного бухгалтера'
  },
  tagline: {
    en: 'VAT week, wages on Wednesday, a director with "two small things" and 48 000 from nowhere',
    ua: 'Тиждень ПДВ, зарплата в середу, директор із «двома дрібницями» і 48 000 нізвідки'
  },
  minutes: 10,
  intro: {
    en: 'You are the only accountant at Dubok, a furniture workshop with 30 employees and a small showroom. It is the second week of October: September has to be closed, the VAT return is due on the 20th, wages are on Wednesday, and the director has ideas.',
    ua: 'Ти єдиний бухгалтер у меблевій майстерні «Дубок»: 30 працівників і невеликий шоурум. Другий тиждень жовтня: треба закрити вересень, декларація з ПДВ до 20-го, зарплата в середу, а в директора є ідеї.'
  },
  goal: {
    en: 'Close September cleanly and on time, without one document you would not show an inspector',
    ua: 'Закрити вересень чисто й вчасно, без жодного документа, який ти не показав би перевірці'
  },
  kpis: [
    { key: 'books', label: { en: 'Clean books', ua: 'Чистота обліку' }, start: 60 },
    { key: 'deadlines', label: { en: 'Deadlines', ua: 'Дедлайни' }, start: 55 },
    { key: 'compliance', label: { en: 'Compliance', ua: 'Законність' }, start: 65 }
  ],
  competencies: [
    {
      key: 'accuracy',
      label: { en: 'Accuracy', ua: 'Точність' },
      skill: { _id: '686282c1aa8e5a92e3bc1408', name: 'Точність' }
    },
    {
      key: 'tax',
      label: { en: 'Tax and reporting', ua: 'Податки та звітність' },
      skill: { _id: '686282c1aa8e5a92e3bc1396', name: 'Знання податкового законодавства' }
    },
    {
      key: 'ethics',
      label: { en: 'Ethics', ua: 'Етика' },
      skill: { _id: '686282c1aa8e5a92e3bc1356', name: 'Відповідальність' }
    },
    {
      key: 'organisation',
      label: { en: 'Organisation', ua: 'Організованість' },
      skill: { _id: '686282c1aa8e5a92e3bc135a', name: 'Організаційні здібності' }
    },
    {
      key: 'communication',
      label: { en: 'Communication', ua: 'Комунікація' },
      skill: { _id: '686282c1aa8e5a92e3bc133c', name: 'Комунікативність' }
    }
  ],
  people: [
    {
      key: 'director',
      name: { en: 'Serhii', ua: 'Сергій Петрович' },
      role: { en: 'Director', ua: 'Директор' },
      initials: 'SP',
      color: '#30b0c7'
    },
    {
      key: 'worker',
      name: { en: 'Mykola', ua: 'Микола' },
      role: { en: 'Carpenter', ua: 'Столяр' },
      initials: 'MY',
      color: '#4f9a04'
    },
    {
      key: 'supplier',
      name: { en: 'Tetiana', ua: 'Тетяна' },
      role: { en: 'Accountant, plywood supplier', ua: 'Бухгалтерка постачальника фанери' },
      initials: 'TE',
      color: '#d5921b'
    },
    {
      key: 'bank',
      name: { en: 'Anna', ua: 'Анна' },
      role: { en: 'Bank account manager', ua: 'Менеджерка банку' },
      initials: 'AN',
      color: '#19857b'
    },
    {
      key: 'foreman',
      name: { en: 'Roman', ua: 'Роман' },
      role: { en: 'Production foreman', ua: 'Начальник цеху' },
      initials: 'RO',
      color: '#e25510'
    }
  ],
  steps: [
    {
      type: 'spot',
      id: 's1',
      at: '08:45',
      comp: ['accuracy'],
      traits: ['C'],
      text: {
        en: 'The plywood supplier\'s invoice for September is in your inbox. Roman has already written "OK, pay" on it. It has 3 mistakes. Tap each one.',
        ua: 'Рахунок від постачальника фанери за вересень у твоїй пошті. Роман уже написав на ньому «ОК, платити». У ньому 3 помилки. Натисни на кожну.'
      },
      mock: {
        kind: 'invoice',
        title: {
          en: 'Invoice No. 118, Lisova Fanera LLC',
          ua: 'Рахунок № 118, ТОВ «Лісова фанера»'
        },
        blocks: [
          {
            id: 'b1',
            kind: 'field',
            label: { en: 'Date', ua: 'Дата' },
            value: '31.09.2026',
            issue: {
              en: 'September has 30 days. A date that does not exist makes the whole document invalid.',
              ua: 'У вересні 30 днів. Дата, якої не існує, робить весь документ недійсним.'
            }
          },
          {
            id: 'b2',
            kind: 'field',
            label: { en: 'Buyer', ua: 'Покупець' },
            value: { en: 'Dubok LLC, VAT payer', ua: 'ТОВ «Дубок», платник ПДВ' }
          },
          {
            id: 'b3',
            kind: 'line',
            label: {
              en: 'Plywood 18 mm, 40 sheets × 1 250',
              ua: 'Фанера 18 мм, 40 листів × 1 250'
            },
            value: '50 000'
          },
          {
            id: 'b4',
            kind: 'line',
            label: { en: 'Edge banding, 200 m × 25', ua: 'Кромка, 200 м × 25' },
            value: '5 000'
          },
          {
            id: 'b5',
            kind: 'total',
            label: { en: 'Subtotal, EUR', ua: 'Разом без ПДВ, грн' },
            value: '55 000'
          },
          {
            id: 'b6',
            kind: 'total',
            label: { en: 'VAT 20%', ua: 'ПДВ 20%' },
            value: '10 000',
            issue: {
              en: '20% of 55 000 is 11 000, not 10 000. Claiming the wrong input VAT is a finding on both sides.',
              ua: '20% від 55 000 — це 11 000, а не 10 000. Неправильний податковий кредит — зауваження для обох сторін.'
            }
          },
          {
            id: 'b7',
            kind: 'total',
            label: { en: 'Total, EUR', ua: 'Усього з ПДВ, грн' },
            value: '67 000',
            issue: {
              en: '55 000 + 11 000 = 66 000; even with their wrong VAT it would be 65 000. The total matches nothing.',
              ua: '55 000 + 11 000 = 66 000; навіть з їхнім неправильним ПДВ було б 65 000. Підсумок не сходиться ні з чим.'
            }
          },
          {
            id: 'b8',
            kind: 'note',
            label: { en: 'Payment terms', ua: 'Умови оплати' },
            value: { en: '10 banking days', ua: '10 банківських днів' }
          },
          {
            id: 'b9',
            kind: 'button',
            label: { en: 'Approve for payment', ua: 'Погодити до оплати' },
            value: ''
          }
        ]
      },
      explain: {
        en: 'An impossible date, VAT of 10 000 where 20% of 55 000 is 11 000, and a total of 67 000 that fits neither 65 000 nor 66 000. One corrected invoice from the supplier fixes all three; paying it as is would send 67 000 out on paper that cannot be booked.',
        ua: 'Неможлива дата, ПДВ 10 000 там, де 20% від 55 000 — це 11 000, і підсумок 67 000, який не сходиться ні з 65 000, ні з 66 000. Один виправлений рахунок від постачальника закриває всі три; оплата як є — це 67 000 за документом, який не можна провести.'
      },
      fx: { ok: { books: 8 }, fail: { books: -6 } }
    },
    {
      type: 'numeric',
      id: 's2',
      at: '09:30',
      comp: ['tax', 'accuracy'],
      traits: ['C', 'I'],
      text: {
        en: 'Time for the September VAT figure. Sales are shown net of VAT; the input VAT is already totalled from registered supplier invoices.',
        ua: 'Час порахувати ПДВ за вересень. Продажі наведено без ПДВ; податковий кредит уже зібрано із зареєстрованих податкових накладних постачальників.'
      },
      data: [
        { label: { en: 'Net sales, EUR', ua: 'Продажі без ПДВ, грн' }, value: '1 240 000' },
        { label: { en: 'VAT rate', ua: 'Ставка ПДВ' }, value: '20%' },
        { label: { en: 'Input VAT, EUR', ua: 'Податковий кредит, грн' }, value: '186 000' }
      ],
      question: {
        en: 'How much VAT is payable for September?',
        ua: 'Скільки ПДВ до сплати за вересень?'
      },
      answer: 62000,
      tolerance: 0.01,
      unit: { en: 'EUR', ua: 'грн' },
      hint: {
        en: 'Output VAT on sales first, then subtract what you can reclaim.',
        ua: 'Спершу податкове зобов’язання з продажів, потім відніми податковий кредит.'
      },
      explain: {
        en: '1 240 000 × 20% = 248 000 output VAT. 248 000 − 186 000 = 62 000 payable. That is the number for the return and for the cash plan on the 30th.',
        ua: '1 240 000 × 20% = 248 000 податкового зобов’язання. 248 000 − 186 000 = 62 000 до сплати. Це цифра і для декларації, і для платіжного плану на 30-те.'
      },
      fx: { ok: { books: 5, deadlines: 3 }, fail: { books: -5 } }
    },
    {
      type: 'chat',
      id: 's3',
      at: '10:30',
      comp: ['ethics', 'communication'],
      traits: ['E', 'C'],
      messages: [
        {
          from: 'director',
          text: {
            en: "Two small things. I bought a TV for home yesterday, 32 000, the receipt is on your desk, put it through as showroom equipment. And the Horizon contract we signed on Friday, date it 28 September so it lands in last month's numbers.",
            ua: 'Дві дрібниці. Я вчора купив телевізор додому, 32 000, чек у тебе на столі, проведи як обладнання для шоуруму. І договір із «Горизонтом», який підписали в п’ятницю, постав 28 вересня, щоб потрапив у минулий місяць.'
          }
        },
        {
          from: 'director',
          text: {
            en: 'It is my company and my money. Just make it tidy.',
            ua: 'Це моя компанія і мої гроші. Просто зроби акуратно.'
          }
        }
      ],
      prompt: { en: 'What do you reply?', ua: 'Що відповідаєш?' },
      options: [
        {
          text: {
            en: "Neither, but here is what works: the TV goes through as your personal purchase, or it really stands in the showroom with a transfer act. The contract keeps Friday's date; if work began in September, the September act shows that.",
            ua: 'Ні перше, ні друге, але є законний варіант: телевізор проводимо як вашу особисту покупку або він справді стоїть у шоурумі з актом передачі. Договір лишається п’ятничним; якщо роботи почались у вересні, це покаже вересневий акт.'
          },
          q: 'best',
          fx: { compliance: 8, trust: 2 },
          fb: {
            en: 'He hears "no" twice and a door each time. A personal expense in company books and a backdated contract are both false entries you would sign; the alternatives give him most of what he wanted, lawfully.',
            ua: 'Він чує двічі «ні» й щоразу двері. Особиста витрата в обліку компанії та договір заднім числом — обидва фальшиві записи, які підписав би ти; альтернативи дають йому більшість бажаного, але законно.'
          }
        },
        {
          text: {
            en: "Say no to both and leave it there: a personal TV is not a company cost, and a contract is dated the day it was signed. If he wants another opinion, the lawyer's number is in the shared contacts.",
            ua: 'Відмовити в обох і на цьому зупинитись: особистий телевізор — не витрата компанії, а договір датують днем підписання. Якщо хоче іншу думку, номер юриста є у спільних контактах компанії.'
          },
          q: 'ok',
          fx: { compliance: 5, trust: -4 },
          fb: {
            en: "Lawful, and it holds. But a bare no with a lawyer's number sends him looking for someone who says yes, and your next month gets harder.",
            ua: 'Законно, і це тримається. Але голе «ні» з номером юриста відправляє його шукати того, хто скаже «так», і наступний місяць стає важчим.'
          }
        },
        {
          text: {
            en: 'Do both as asked. It is his company, 32 000 is small, and a four-day shift on a contract date is the kind of thing nobody ever checks, especially not in a workshop this size.',
            ua: 'Зробити обидва, як просить. Компанія його, 32 000 — дрібниця, а зсув дати договору на чотири дні ніхто ніколи не перевіряє, тим паче в майстерні такого розміру.'
          },
          q: 'bad',
          fx: { compliance: -15, books: -6 },
          fb: {
            en: 'Both entries carry your signature, not his. An inspector checks dates against bank payments and signatures, and "small" is not a legal category.',
            ua: 'Обидва записи несуть твій підпис, а не його. Перевірка звіряє дати з банківськими платежами й підписами, а «дрібниця» — не юридична категорія.'
          },
          later: {
            after: 2,
            note: {
              en: "The supplier's reconciliation shows the Horizon advance arriving on 3 October. Your September contract now contradicts the bank.",
              ua: 'Звірка показує аванс від «Горизонту» 3 жовтня. Твій вересневий договір тепер суперечить банку.'
            },
            fx: { compliance: -6 }
          }
        },
        {
          text: {
            en: 'Agree to the TV as showroom equipment, since a showroom can plausibly have one, but refuse the date change; half a yes keeps the peace and the books mostly clean.',
            ua: 'Погодитись на телевізор як обладнання шоуруму, адже в шоурумі він цілком може стояти, але відмовити в зміні дати; половина «так» береже мир і майже чистий облік.'
          },
          q: 'bad',
          fx: { compliance: -8, trust: 1 },
          fb: {
            en: 'A TV that is at his home is at his home; "plausible" is what you say when you know it is false. You bought peace with your own signature.',
            ua: 'Телевізор, який у нього вдома, — удома; «цілком може» кажуть, коли знають, що це неправда. Ти купив мир власним підписом.'
          }
        }
      ],
      principle: {
        en: 'Refuse with an alternative; your signature is the thing being asked for.',
        ua: 'Відмовляй, пропонуючи альтернативу; просять саме твій підпис.'
      }
    },
    {
      type: 'queue',
      id: 's4',
      at: '11:20',
      comp: ['organisation', 'communication'],
      traits: ['C', 'S'],
      text: {
        en: 'Four requests wait after the conversation with the director. Open them in the order you think is right and answer each.',
        ua: 'Після розмови з директором чекають чотири запити. Відкрий їх у тому порядку, який вважаєш правильним, і відповідай на кожен.'
      },
      tickets: [
        {
          id: 't1',
          from: 'bank',
          urgency: 3,
          sla: { en: 'today 15:00', ua: 'сьогодні до 15:00' },
          subject: { en: 'Documents for payment No. 412', ua: 'Документи до платежу № 412' },
          body: {
            en: "Financial monitoring has flagged Friday's 180 000 payment to a new supplier. We need the contract and the invoice by 15:00 today, or the payment is returned.",
            ua: 'Фінансовий моніторинг зупинив п’ятничний платіж на 180 000 новому постачальнику. Потрібні договір і рахунок до 15:00 сьогодні, інакше платіж повернуть.'
          },
          options: [
            {
              text: {
                en: 'Send the contract and the invoice as scans within the hour, with a one-line note on what the supplier delivers, and confirm receipt by phone.',
                ua: 'Надіслати скани договору й рахунку протягом години, з одним рядком про те, що постачає цей контрагент, і підтвердити отримання телефоном.'
              },
              q: 'best',
              fx: { deadlines: 5, compliance: 3 },
              fb: {
                en: 'A blocked payment to a new supplier is a late delivery and a reputation. An hour of scanning beats a week of explaining.',
                ua: 'Заблокований платіж новому постачальнику — це зірвана поставка й репутація. Година сканування краща за тиждень пояснень.'
              }
            },
            {
              text: {
                en: 'Reply that the documents will be there by 14:30 and get to it after the reconciliation act, which is already open on your screen anyway.',
                ua: 'Відповісти, що документи будуть до 14:30, і взятися після акта звірки, який і так уже відкритий на екрані.'
              },
              q: 'ok',
              fx: { deadlines: 1, energy: -3 },
              fb: {
                en: 'You will probably make it. But a hard 15:00 from a bank is not the thing to put behind a reconciliation with no deadline today.',
                ua: 'Ти, мабуть, встигнеш. Але жорстке 15:00 від банку — не те, що ставлять після звірки, у якої сьогодні дедлайну немає.'
              }
            },
            {
              text: {
                en: 'Ask Anna why a normal supplier payment is being questioned and whether the bank can release it first; the documents are in the archive and take time.',
                ua: 'Спитати Анну, чому звичайний платіж постачальнику ставлять під сумнів і чи може банк спершу його пропустити; документи в архіві, це довго.'
              },
              q: 'bad',
              fx: { deadlines: -6, trust: -3 },
              fb: {
                en: 'The bank does not negotiate monitoring rules with clients. The payment bounces at 15:01, and the supplier calls the director, not you.',
                ua: 'Банк не обговорює правила моніторингу з клієнтами. Платіж повертається о 15:01, а постачальник дзвонить директору, не тобі.'
              }
            }
          ]
        },
        {
          id: 't2',
          from: 'supplier',
          urgency: 2,
          sla: { en: 'this week', ua: 'цього тижня' },
          subject: { en: 'Reconciliation act, 3rd quarter', ua: 'Акт звірки за 3 квартал' },
          body: {
            en: 'Our act shows you owe 142 000; your last letter said 130 000. Could you sign today? Our auditors come in on Thursday.',
            ua: 'За нашим актом ваш борг 142 000; у вашому останньому листі було 130 000. Підпишете сьогодні? У четвер у нас аудитори.'
          },
          options: [
            {
              text: {
                en: 'Reply that you will match the 12 000 line by line today and send either a signed act or a list of the differing documents by tomorrow morning.',
                ua: 'Відповісти, що сьогодні звіриш розбіжність у 12 000 по документах і до завтрашнього ранку надішлеш або підписаний акт, або перелік документів, що не сходяться.'
              },
              q: 'best',
              fx: { books: 4, trust: 2 },
              fb: {
                en: 'You never sign a balance you have not reconciled, and you never leave the other side without a date. Tomorrow morning still beats her Thursday.',
                ua: 'Сальдо, яке не звірено, не підписують, а іншу сторону не лишають без дати. Завтрашній ранок усе одно раніше за її четвер.'
              }
            },
            {
              text: {
                en: 'Sign it with a note "subject to invoice 118 being corrected"; the difference is probably that invoice anyway, and she needs the paper today.',
                ua: 'Підписати з приміткою «за умови виправлення рахунку № 118»; розбіжність, мабуть, саме в ньому, а папір їй потрібен сьогодні.'
              },
              q: 'ok',
              fx: { trust: 2, books: -3 },
              fb: {
                en: '"Probably" on a signed act is how 12 000 becomes a dispute next quarter. A note helps; a reconciliation settles.',
                ua: '«Мабуть» на підписаному акті — так 12 000 стають суперечкою наступного кварталу. Примітка допомагає; звірка вирішує.'
              }
            },
            {
              text: {
                en: 'Sign and send it; 12 000 on a 142 000 balance is within normal noise, and her auditors are her problem, not yours.',
                ua: 'Підписати й надіслати; 12 000 на сальдо 142 000 — звичайна похибка, а її аудитори — її проблема, не твоя.'
              },
              q: 'bad',
              fx: { books: -6, compliance: -3 },
              fb: {
                en: 'There is no "normal noise" in a reconciliation; a signed act is an admission of debt. You just confirmed 12 000 you may not owe.',
                ua: 'У звірці не буває «звичайної похибки»; підписаний акт — це визнання боргу. Ти щойно підтвердив 12 000, яких, можливо, не винен.'
              }
            }
          ]
        },
        {
          id: 't3',
          from: 'worker',
          urgency: 2,
          sla: { en: 'today', ua: 'сьогодні' },
          subject: { en: 'Advance before Wednesday', ua: 'Аванс до середи' },
          body: {
            en: 'My boiler broke. Could I get 8 000 from my wages today instead of Wednesday? Roman said to ask you.',
            ua: 'У мене зламався котел. Можна отримати 8 000 із зарплати сьогодні, а не в середу? Роман сказав спитати у вас.'
          },
          options: [
            {
              text: {
                en: 'Tell him yes if the director signs the advance order, draft the order now and send it to Serhii; the money can go by bank transfer today.',
                ua: 'Сказати «так», якщо директор підпише наказ про аванс, підготувати наказ зараз і надіслати Сергію Петровичу; гроші можуть піти переказом сьогодні.'
              },
              q: 'best',
              fx: { trust: 3, books: 2 },
              fb: {
                en: 'An advance is lawful when it is documented and approved. Ten minutes of paper gets a colleague a working boiler and keeps the payroll clean.',
                ua: 'Аванс законний, коли він оформлений і погоджений. Десять хвилин паперів дають колезі робочий котел і чистий розрахунок зарплати.'
              }
            },
            {
              text: {
                en: 'Say it is better to wait until Wednesday, as an off-cycle payment means extra paperwork and tax calculations this week, when there is no time for it.',
                ua: 'Сказати, що краще дочекатися середи, бо позаплановий платіж — це додаткові папери й розрахунок податків цього тижня, коли на це немає часу.'
              },
              q: 'ok',
              fx: { trust: -3 },
              fb: {
                en: 'True about the paperwork and tone-deaf about the boiler. Thirty people watch how you treat the first one who asks.',
                ua: 'Правда про папери і глухота до котла. Тридцять людей дивляться, як ти ставишся до першого, хто попросив.'
              }
            },
            {
              text: {
                en: 'Give him 8 000 from the petty cash now and sort out the paperwork at payroll; it is his money anyway, and the boiler will not wait for a signature.',
                ua: 'Видати 8 000 з каси зараз, а папери оформити при розрахунку зарплати; це ж і так його гроші, а котел підпису не чекатиме.'
              },
              q: 'bad',
              fx: { compliance: -8, books: -4 },
              fb: {
                en: 'Cash out of the till with no order and no signature is a shortfall until proven otherwise. "His money anyway" is not what the cash count will say.',
                ua: 'Готівка з каси без наказу й підпису — це нестача, поки не доведено інше. «І так його гроші» — не те, що покаже інвентаризація каси.'
              }
            }
          ]
        },
        {
          id: 't4',
          from: { en: 'Courier service', ua: 'Кур’єрська служба' },
          urgency: 1,
          sla: { en: '5 days', ua: '5 днів' },
          subject: { en: 'Invoice for September deliveries', ua: 'Рахунок за доставки у вересні' },
          body: {
            en: 'Invoice No. 2290 for 14 deliveries, 4 150 in total, attached. Please pay within 5 banking days.',
            ua: 'Рахунок № 2290 за 14 доставок, разом 4 150, у вкладенні. Просимо оплатити протягом 5 банківських днів.'
          },
          options: [
            {
              text: {
                en: "Check it against the 14 delivery notes, put it in Thursday's payment batch and file the invoice; reply only if something does not match.",
                ua: 'Звірити з 14 накладними на доставку, поставити в четвергову платіжку й підшити рахунок; відповідати лише якщо щось не сходиться.'
              },
              q: 'best',
              fx: { deadlines: 2, books: 2 },
              fb: {
                en: 'Routine gets a routine: check, batch, file. Five days of slack means it does not touch today.',
                ua: 'Рутина отримує рутину: звірити, у пакет, підшити. П’ять днів запасу означають, що сьогодні це не чіпає.'
              }
            },
            {
              text: {
                en: 'Pay it now while it is in front of you; 4 150 is small, and that is one fewer open item on a week like this one.',
                ua: 'Оплатити зараз, поки перед очима; 4 150 — дрібниця, і на один відкритий пункт менше в такий тиждень, як цей.'
              },
              q: 'ok',
              fx: { energy: -2, books: -1 },
              fb: {
                en: 'Done, but unchecked. Small invoices are where double-billed deliveries hide, and a Thursday batch is cheaper than a Monday payment.',
                ua: 'Зроблено, але не перевірено. Саме в дрібних рахунках ховаються двічі виставлені доставки, а четверговий пакет дешевший за понеділковий платіж.'
              }
            },
            {
              text: {
                en: 'Leave it in the inbox until next week; a courier will not stop deliveries over 4 150, and the week is already full enough.',
                ua: 'Лишити в пошті до наступного тижня; кур’єр не зупинить доставки через 4 150, а тиждень і так повний по вінця.'
              },
              q: 'bad',
              fx: { deadlines: -3, trust: -2 },
              fb: {
                en: 'Next week it is overdue, then it is a late fee, then it is the director asking why the courier called. Low urgency still gets a slot.',
                ua: 'Наступного тижня він прострочений, потім пеня, потім директор питає, чому дзвонив кур’єр. Низька терміновість усе одно отримує місце в плані.'
              }
            }
          ]
        }
      ]
    },
    {
      type: 'triage',
      id: 's5',
      at: '13:00',
      comp: ['organisation', 'tax'],
      traits: ['C', 'I'],
      text: {
        en: 'Monday afternoon. Five deadlines sit in the week ahead. Put them in the order you will work on them.',
        ua: 'Понеділок після обіду. Попереду п’ять дедлайнів тижня. Розстав їх у порядку, в якому візьмешся.'
      },
      items: [
        {
          id: 'wages',
          title: { en: 'Wages and payroll taxes', ua: 'Зарплата й податки із зарплати' },
          detail: {
            en: 'Payment is Wednesday for 30 people; payroll taxes go the same day. Two timesheets from the shop floor are still missing.',
            ua: 'Виплата в середу на 30 людей; ПДФО, військовий збір і ЄСВ — того ж дня. Двох табелів із цеху ще немає.'
          }
        },
        {
          id: 'tn',
          title: {
            en: 'Issue remaining September VAT invoices',
            ua: 'Зареєструвати вересневі податкові накладні'
          },
          detail: {
            en: 'Six invoices from the second half of September still unissued. Deadline Thursday; late issue carries a penalty and customers cannot reclaim.',
            ua: 'Шість накладних за другу половину вересня ще не зареєстровано в ЄРПН. Дедлайн четвер; за прострочення штраф, а клієнти не отримують податковий кредит.'
          }
        },
        {
          id: 'vat',
          title: { en: 'VAT return for September', ua: 'Декларація з ПДВ за вересень' },
          detail: {
            en: 'Due on the 20th, next Tuesday. The figure is ready, the return itself takes half a day.',
            ua: 'До 20-го, наступного вівторка. Цифра готова, сама декларація — пів дня.'
          }
        },
        {
          id: 'pay',
          title: { en: 'Supplier payment under contract', ua: 'Оплата постачальнику за договором' },
          detail: {
            en: 'Due Friday by contract; the supplier has already said a few days is fine if you tell them.',
            ua: 'За договором до п’ятниці; постачальник уже сказав, що кілька днів не проблема, якщо попередити.'
          }
        },
        {
          id: 'stat',
          title: { en: 'Quarterly statistics report', ua: 'Квартальний звіт у статистику' },
          detail: {
            en: "Due next Friday. Two hours with last quarter's file as a template.",
            ua: 'До наступної п’ятниці. Дві години з файлом минулого кварталу як шаблоном.'
          }
        }
      ],
      correctOrder: ['wages', 'tn', 'vat', 'pay', 'stat'],
      explain: {
        en: "Wednesday's wages come first: 30 people and the tax office wait on the same day, and two timesheets have to be chased today. Thursday's VAT invoices have a penalty and a customer behind each one. The return is a legal date on the 20th, so it follows. The supplier has offered slack; statistics has a template and a week.",
        ua: 'Спершу середова зарплата: 30 людей і податкова чекають того ж дня, а два табелі треба вибити вже сьогодні. За четверговими накладними — штраф і клієнт за кожною. Декларація — законна дата 20-го, тож іде наступною. Постачальник сам дав запас; у статистики є шаблон і тиждень.'
      },
      fx: { ok: { deadlines: 6 }, fail: { deadlines: -5, energy: -3 } }
    },
    {
      type: 'choice',
      id: 's6',
      at: '14:00',
      comp: ['ethics', 'accuracy'],
      traits: ['I', 'C'],
      text: {
        en: 'In today\'s bank statement there is an incoming 48 000 from "Vector Plus LLC", purpose: "payment for furniture, contract 17/09". You have no such contract, no such client and no invoice for that amount. Roman says he has never heard of them.',
        ua: 'У сьогоднішній виписці — надходження 48 000 від ТОВ «Вектор Плюс», призначення: «оплата за меблі, договір 17/09». Такого договору, клієнта й рахунку на цю суму в тебе немає. Роман каже, що ніколи про них не чув.'
      },
      options: [
        {
          text: {
            en: 'Book it as unidentified receipts, not revenue, write to the bank and the payer the same day, and return it within the deadline the rules set if nobody can show a contract. Tell the director not to spend it.',
            ua: 'Провести як нез’ясовані надходження, а не дохід, того ж дня написати в банк і платнику, і повернути у строк, передбачений правилами, якщо ніхто не покаже договору. Попередити директора, щоб не витрачав.'
          },
          q: 'best',
          fx: { compliance: 7, books: 4 },
          fb: {
            en: "Money you cannot match to a document is not yours yet. Querying it in writing and returning it on time keeps the account clean and the company out of someone else's mistake, or scheme.",
            ua: 'Гроші, які не зіставляються з документом, ще не твої. Письмовий запит і вчасне повернення тримають рахунок чистим, а компанію — поза чужою помилкою чи схемою.'
          }
        },
        {
          text: {
            en: 'Ask Serhii whether he agreed anything with a Vector Plus and hold the money untouched until he answers; he sometimes signs deals before telling you, and this may be one of them.',
            ua: 'Спитати Сергія Петровича, чи не домовлявся він із якимось «Вектор Плюс», і не чіпати гроші, поки не відповість; він іноді підписує угоди раніше, ніж каже тобі, і це може бути саме той випадок.'
          },
          q: 'ok',
          fx: { books: 2, deadlines: -2 },
          fb: {
            en: 'A sensible first step, and sometimes the whole answer. But the clock on returning an erroneous transfer runs from today, not from when the director replies.',
            ua: 'Розумний перший крок, іноді й уся відповідь. Але строк повернення помилкового платежу спливає від сьогодні, а не від моменту, коли відповість директор.'
          }
        },
        {
          text: {
            en: "Book it as a customer advance; a new client clearly paid early, the contract will turn up in someone's drawer, and the cash is welcome in VAT week.",
            ua: 'Провести як аванс від покупця; новий клієнт явно заплатив наперед, договір знайдеться в чиїйсь шухляді, а гроші в тиждень ПДВ дуже доречні.'
          },
          q: 'bad',
          fx: { compliance: -10, books: -6 },
          fb: {
            en: 'An advance creates a VAT liability and a debt to a company you have never met. If it was a mistake or a laundering test, you just booked it as yours.',
            ua: 'Аванс створює податкове зобов’язання з ПДВ і борг перед компанією, якої ти ніколи не бачив. Якщо це помилка або перевірка на відмивання, ти щойно оформив її як свою.'
          }
        },
        {
          text: {
            en: 'Leave it on the statement until the month-end close; the bank will sort out its own mistake, and the payer will write if they want the money back.',
            ua: 'Лишити у виписці до закриття місяця й нічого не чіпати; банк сам розбереться зі своєю помилкою, а платник напише, якщо захоче гроші назад, таке вже бувало.'
          },
          q: 'bad',
          fx: { compliance: -6, deadlines: -3 },
          fb: {
            en: 'Banks do not reverse credits on their own, and a payer who does not write may be hoping you spend it. Unidentified money has a return deadline you just let pass.',
            ua: 'Банки самі не скасовують зарахування, а платник, який не пише, можливо, сподівається, що ти їх витратиш. У нез’ясованих грошей є строк повернення, який ти щойно пропустив.'
          }
        }
      ],
      principle: {
        en: 'Money without a document is not income. Query it today.',
        ua: 'Гроші без документа — не дохід. Запитуй сьогодні.'
      }
    },
    {
      type: 'numeric',
      id: 's7',
      at: '15:00',
      comp: ['accuracy'],
      traits: ['C', 'I'],
      text: {
        en: "Roman's new CNC router went into service on 1 September. You book straight-line depreciation monthly.",
        ua: 'Новий фрезерний верстат із ЧПК у цеху Романа введено в експлуатацію 1 вересня. Амортизацію нараховуєш прямолінійно, щомісяця.'
      },
      data: [
        { label: { en: 'Cost, EUR', ua: 'Первісна вартість, грн' }, value: '720 000' },
        { label: { en: 'Residual value, EUR', ua: 'Ліквідаційна вартість, грн' }, value: '36 000' },
        {
          label: { en: 'Useful life', ua: 'Строк корисного використання' },
          value: { en: '6 years', ua: '6 років' }
        }
      ],
      question: {
        en: 'What is the depreciation charge for September?',
        ua: 'Яка сума амортизації за вересень?'
      },
      answer: 9500,
      tolerance: 0.01,
      unit: { en: 'EUR', ua: 'грн' },
      hint: {
        en: 'Depreciable amount over the useful life in months.',
        ua: 'Вартість, що амортизується, поділи на строк у місяцях.'
      },
      explain: {
        en: '720 000 − 36 000 = 684 000 to depreciate. 6 years = 72 months. 684 000 ÷ 72 = 9 500 a month, the same figure every month until August 2032.',
        ua: '720 000 − 36 000 = 684 000 до амортизації. 6 років = 72 місяці. 684 000 ÷ 72 = 9 500 на місяць, та сама сума щомісяця до серпня 2032-го.'
      },
      fx: { ok: { books: 5 }, fail: { books: -5 } }
    },
    {
      type: 'allocate',
      id: 's8',
      at: '16:00',
      comp: ['organisation', 'tax'],
      traits: ['C', 'E'],
      text: {
        en: 'Ten working days remain to close September and file everything. Split your time across the four blocks of work.',
        ua: 'Залишається десять робочих днів, щоб закрити вересень і все подати. Розподіли свій час між чотирма блоками роботи.'
      },
      buckets: [
        {
          id: 'reporting',
          label: { en: 'Tax reporting', ua: 'Податкова звітність' },
          hint: {
            en: 'VAT return, VAT invoices, payroll tax reports. Every one has a date and a penalty.',
            ua: 'Декларація з ПДВ, податкові накладні, звітність із зарплатних податків. У кожної є дата і штраф.'
          },
          best: 35
        },
        {
          id: 'payroll',
          label: { en: 'Payroll', ua: 'Зарплата' },
          hint: {
            en: 'Thirty people on Wednesday. A mistake here is noticed within the hour.',
            ua: 'Тридцять людей у середу. Помилку тут помічають протягом години.'
          },
          best: 25
        },
        {
          id: 'recon',
          label: { en: 'Reconciliations', ua: 'Звірки з контрагентами' },
          hint: {
            en: 'Fourteen suppliers, two with open differences. Auditors read these first.',
            ua: 'Чотирнадцять постачальників, у двох є розбіжності. Аудитори читають їх першими.'
          },
          best: 25
        },
        {
          id: 'archive',
          label: { en: 'Archiving and filing', ua: 'Архів і підшивка' },
          hint: {
            en: 'No deadline this month. The annual audit is in January.',
            ua: 'Цього місяця дедлайну немає. Річний аудит у січні.'
          },
          best: 15
        }
      ],
      explain: {
        en: 'Reporting gets the largest share because every item has a legal date and a penalty. Payroll and reconciliations split the middle: one is watched by thirty people, the other by the auditors. Archiving has no deadline, but a slice now stops January from becoming a crisis.',
        ua: 'Звітність отримує найбільшу частку, бо в кожного пункту є законна дата і штраф. Зарплата й звірки ділять середину: за однією стежать тридцять людей, за іншими — аудитори. В архіву дедлайну немає, але частка зараз не дає січню стати кризою.'
      },
      fx: { ok: { deadlines: 6, books: 2 }, fail: { deadlines: -5 } }
    }
  ],
  verdict: {
    bands: [
      {
        min: 0.8,
        title: {
          en: 'The accountant every director wants and half of them fear',
          ua: 'Бухгалтер, якого хоче кожен директор і половина боїться'
        },
        text: {
          en: "You refused with alternatives, matched money to documents and ranked deadlines by penalty and people. That is a chief accountant's set of habits.",
          ua: 'Ти відмовляв з альтернативами, зіставляв гроші з документами й ранжував дедлайни за штрафами й людьми. Це набір звичок головного бухгалтера.'
        }
      },
      {
        min: 0.6,
        title: {
          en: 'Clean books, a few late evenings',
          ua: 'Чистий облік, кілька пізніх вечорів'
        },
        text: {
          en: 'The numbers were right; the misses were about order, doing the open task instead of the one with a clock on it.',
          ua: 'Цифри були правильні; промахи — про порядок: ти робив відкриту задачу замість тієї, що з годинником.'
        }
      },
      {
        min: 0.4,
        title: {
          en: 'The returns would go in, the director would get his way',
          ua: 'Звітність би подалась, а директор домігся б свого'
        },
        text: {
          en: 'You tend to buy peace with your signature. Re-read the feedback on the TV and on the unknown payment.',
          ua: 'Ти схильний купувати спокій власним підписом. Перечитай фідбек про телевізор і про невідомий платіж.'
        }
      },
      {
        min: 0,
        title: {
          en: 'This month would have ended with a fine and a resignation',
          ua: 'Цей місяць закінчився б штрафом і заявою на звільнення'
        },
        text: {
          en: 'Several choices treated documents as formalities. In this job, the document is the fact.',
          ua: 'Кілька рішень трактували документи як формальність. У цій роботі документ і є факт.'
        }
      }
    ],
    reality: [
      {
        en: 'Roughly 60% of the month is routine entry and reconciliation; the interesting decisions fit into two or three days around the 15th and the 20th.',
        ua: 'Близько 60% місяця — рутинне проведення й звірки; цікаві рішення вміщуються у два-три дні навколо 15-го й 20-го.'
      },
      {
        en: 'Every week someone asks you to make something "tidy" that is not. Saying no with a lawful alternative is a third of the skill.',
        ua: 'Щотижня хтось просить зробити «акуратно» те, що акуратним не є. Відмова із законною альтернативою — третина професії.'
      },
      {
        en: 'Tax rules change several times a year; expect an evening a month reading updates nobody will thank you for knowing.',
        ua: 'Податкові правила змінюються кілька разів на рік; розраховуй на вечір щомісяця на читання оновлень, за знання яких ніхто не подякує.'
      }
    ]
  }
};
